// Per-user alert switches (Settings → Notifications).
//
// Three categories, all ON by default:
//   textInbox — a customer text landing in the Text Inbox
//   email     — a new email in the shared mailbox
//   chat      — order chat, direct chat and group messages, mentions, reactions
//
// OFF silences the ACTIVE alerts for that category — toast, sound, bell row, OS
// popup and the closed-tab push. It deliberately does NOT hide the unread badges
// in the sidebar: those are passive, and a dispatcher who muted Email still
// wants to see at a glance that mail is waiting. Orders, stock and cash alerts
// are never affected — they are the job, not a channel preference.
//
// Storage: `user_notification_prefs` (migration 0070), one row per user, so the
// choice follows the person across browsers and the push function can honour
// it. localStorage mirrors it so the very first alert after a reload is already
// gated, and it is the whole store while the migration is not applied yet.
import { useSyncExternalStore } from 'react';
import { supabase, USE_SUPABASE } from './supabaseClient';
import { isMissingSchema } from './roomActionsParity';
import { UserRole } from '../types';

export type NotificationCategory = 'textInbox' | 'email' | 'chat';
export type NotificationPrefs = Record<NotificationCategory, boolean>;

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = { textInbox: true, email: true, chat: true };
export const NOTIFICATION_PREFS_TABLE = 'user_notification_prefs';

const CHAT_TYPES = new Set([
  'new_message', 'direct_message', 'room_message', 'room_mention', 'room_reaction',
  'message_reaction', 'direct_message_reaction',
]);

/** Which switch governs this alert; null = not switchable (orders, stock…).
 *  Mirrored in netlify/functions/_notification-prefs.js — keep them identical. */
export function notificationCategoryOf(type: string | undefined | null): NotificationCategory | null {
  if (!type) return null;
  if (type === 'forwarded_sms') return 'textInbox';
  if (type === 'email') return 'email';
  if (CHAT_TYPES.has(type)) return 'chat';
  return null;
}

/** Stores and drivers never receive Email / Text Inbox alerts, so they get no card. */
export function canConfigureNotifications(role: UserRole | string | undefined | null): boolean {
  return !!role && role !== UserRole.STORE && role !== UserRole.DRIVER;
}

// ── module store ───────────────────────────────────────────────────────────
let current: NotificationPrefs = DEFAULT_NOTIFICATION_PREFS;
let currentUserId: string | null = null;
const listeners = new Set<() => void>();
const lsKey = (uid: string) => `notifPrefs.v1:${uid}`;

function setCurrent(next: NotificationPrefs) {
  if (next.textInbox === current.textInbox && next.email === current.email && next.chat === current.chat) return;
  current = next;
  listeners.forEach((l) => l());
}
function readLocal(uid: string): NotificationPrefs | null {
  try {
    const raw = localStorage.getItem(lsKey(uid));
    if (!raw) return null;
    const p = JSON.parse(raw);
    return { textInbox: p.textInbox !== false, email: p.email !== false, chat: p.chat !== false };
  } catch { return null; }
}
function writeLocal(uid: string, p: NotificationPrefs) {
  try { localStorage.setItem(lsKey(uid), JSON.stringify(p)); } catch { /* private mode */ }
}
const fromRow = (r: any): NotificationPrefs => ({
  textInbox: r?.text_inbox !== false, email: r?.email !== false, chat: r?.chat !== false,
});

export const getNotificationPrefs = (): NotificationPrefs => current;

/** True when the user switched this alert's category off. */
export function isNotificationMuted(type: string | undefined | null): boolean {
  const cat = notificationCategoryOf(type);
  return cat ? !current[cat] : false;
}

/** Call when the signed-in user changes. Local copy first (instant), then the
 *  server row; a missing table or any read error keeps the local copy. */
export async function loadNotificationPrefs(userId: string | null | undefined, role?: UserRole | string | null): Promise<void> {
  currentUserId = userId || null;
  if (!userId || !canConfigureNotifications(role)) { setCurrent(DEFAULT_NOTIFICATION_PREFS); return; }
  setCurrent(readLocal(userId) ?? DEFAULT_NOTIFICATION_PREFS);
  if (!USE_SUPABASE) return;
  try {
    const { data, error } = await supabase().from(NOTIFICATION_PREFS_TABLE)
      .select('text_inbox,email,chat').eq('user_id', userId).maybeSingle();
    if (error || currentUserId !== userId) return; // missing table / offline → local copy stands
    if (data) { const p = fromRow(data); writeLocal(userId, p); setCurrent(p); }
  } catch { /* background read — never surfaces */ }
}

/** Flip one switch. Applies at once; the server write follows. Resolves false
 *  only when the write failed for a reason other than the migration being
 *  absent (then the choice still holds on this browser). */
export async function setNotificationPref(category: NotificationCategory, on: boolean): Promise<boolean> {
  const uid = currentUserId;
  if (!uid) return false;
  const next = { ...current, [category]: on };
  writeLocal(uid, next);
  setCurrent(next);
  if (!USE_SUPABASE) return true;
  const { error } = await supabase().from(NOTIFICATION_PREFS_TABLE).upsert(
    { user_id: uid, text_inbox: next.textInbox, email: next.email, chat: next.chat, updated_at: new Date().toISOString() },
    { onConflict: 'user_id' },
  );
  if (error && !isMissingSchema(error)) return false;
  return true;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export function useNotificationPrefs(): NotificationPrefs {
  return useSyncExternalStore(subscribe, getNotificationPrefs, getNotificationPrefs);
}

/** Test seam. */
export function _resetNotificationPrefs(): void {
  current = DEFAULT_NOTIFICATION_PREFS; currentUserId = null; listeners.clear();
}
